import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '@mui/material/styles';
import { Box, Typography, Button, Chip, useMediaQuery, CircularProgress, Snackbar, Alert } from '@mui/material';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import FlagIcon from '@mui/icons-material/OutlinedFlag';
import SegmentedControl from '../components/domain/SegmentedControl';
import api from '../api/client';

const TABS = [
  { value: 'all', label: 'Wszystkie' },
  { value: 'active', label: 'Aktywne' },
  { value: 'completed', label: 'Zakończone' },
];

const STATUS_CHIP = {
  active: { label: 'Aktywna', color: 'success' },
  completed: { label: 'Zakończona', color: 'default' },
  draft: { label: 'Draft', color: 'default' },
  scheduled: { label: 'Zaplanowana', color: 'info' },
  registration_open: { label: 'W trakcie zapisów', color: 'info' },
  wind_down: { label: 'Rozliczenie', color: 'warning' },
  paused: { label: 'Wstrzymana', color: 'warning' },
  cancelled: { label: 'Odwołana', color: 'error' },
};

function formatDate(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso;
  return d.toLocaleDateString('pl-PL', { day: 'numeric', month: 'short', year: 'numeric' });
}

function formatDateRange(raw) {
  if (!raw) return '—';
  const parts = raw.split(/\s*[–—-]\s*/);
  if (parts.length !== 2) return raw;
  return `${formatDate(parts[0].trim())} – ${formatDate(parts[1].trim())}`;
}

function MiniBarChart({ data = [], height = 40 }) {
  const theme = useTheme();
  if (!data.length) return <Box sx={{ height }} />;
  const max = Math.max(...data);
  if (!max) return <Box sx={{ height }} />;
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height }}>
      {data.map((val, i) => (
        <Box
          key={i}
          sx={{
            flex: 1,
            height: `${(val / max) * 100}%`,
            bgcolor: i === data.length - 1 ? 'primary.main' : theme.palette.action.selected,
            borderRadius: 1,
            minHeight: 4,
            transition: 'height 240ms ease',
          }}
        />
      ))}
    </Box>
  );
}

function Reports() {
  const theme = useTheme();
  const navigate = useNavigate();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [activeTab, setActiveTab] = useState('all');
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [error, setError] = useState(null);

  useEffect(() => {
    api.get('/api/reports/campaigns')
      .then((data) => {
        const list = Array.isArray(data) ? data : [];
        setCampaigns(list.map((c) => ({ ...c, type: c.type || 'Kampania' })));
      })
      .catch((err) => {
        setError(err.body?.error || err.message || 'Błąd pobierania raportów');
        setCampaigns([]);
      })
      .finally(() => setLoading(false));
  }, []);

  const filteredCampaigns = campaigns.filter((c) => activeTab === 'all' || c.status === activeTab);

  const totalParticipants = campaigns.reduce((sum, c) => sum + (c.joined || 0), 0);
  const avgEngagement = campaigns.length > 0
    ? `${Math.round(campaigns.reduce((sum, c) => sum + parseInt(c.conversionRate || '0', 10), 0) / campaigns.length)}%`
    : '0%';
  const popularType = campaigns.length > 0 ? (campaigns[0].type || 'Kampania') : '—';

  const handleExport = (campaignId) => {
    window.open(`${import.meta.env.VITE_API_URL}/api/reports/export/${campaignId}`, '_blank');
    setSnackbar({ open: true, message: 'Pobieranie raportu CSV…', severity: 'success' });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" sx={{ fontFamily: theme.typography.h1.fontFamily, fontSize: { xs: '1.5rem', sm: '2rem' }, mb: 0.5 }}>
          Raporty
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Podsumowanie efektów kampanii w organizacji
        </Typography>
      </Box>

      {/* Aggregate summary */}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, mb: 4 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <TrendingUpIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
              {avgEngagement}
            </Typography>
            <Typography variant="caption" color="text.secondary">Średni engagement</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <FlagIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
              {popularType}
            </Typography>
            <Typography variant="caption" color="text.secondary">Najpopularniejszy typ</Typography>
          </Box>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <GroupOutlinedIcon color="primary" />
          <Box>
            <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
              {totalParticipants.toLocaleString('pl-PL')}
            </Typography>
            <Typography variant="caption" color="text.secondary">Łącznie uczestników</Typography>
          </Box>
        </Box>
      </Box>

      {/* Segmented Control */}
      <Box sx={{ mb: 3 }}>
        <SegmentedControl options={TABS} value={activeTab} onChange={setActiveTab} />
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>
      )}

      {/* Campaign report cards */}
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 4 }}>
        {filteredCampaigns.length === 0 && !error && (
          <Box sx={{ p: 4, textAlign: 'center', borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`, border: `1px solid ${theme.palette.divider}`, bgcolor: 'background.paper' }}>
            <Typography variant="body1" color="text.secondary">Brak kampanii do raportowania</Typography>
          </Box>
        )}
        {filteredCampaigns.map((campaign) => {
          const status = STATUS_CHIP[campaign.status] || { label: campaign.status, color: 'default' };
          return (
            <Box
              key={campaign.id}
              sx={{
                p: 3,
                borderRadius: `${theme.tokens?.primitive?.radius?.card ?? 20}px`,
                border: `1px solid ${theme.palette.divider}`,
                bgcolor: 'background.paper',
                display: 'flex',
                flexDirection: { xs: 'column', md: 'row' },
                gap: 3,
                alignItems: { md: 'center' },
                cursor: 'pointer',
                transition: 'box-shadow 160ms ease',
                '&:hover': { boxShadow: theme.tokens?.semantic?.shadow?.soft },
              }}
              onClick={() => navigate(`/campaigns/${campaign.id}`)}
            >
              {/* Title block */}
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {campaign.title}
                  </Typography>
                  <Chip label={status.label} color={status.color} size="small" />
                </Box>
                <Typography variant="caption" color="text.secondary">
                  {campaign.type} · {formatDateRange(campaign.dates)}
                </Typography>
              </Box>

              {/* Metrics */}
              <Box sx={{ display: 'flex', gap: 3, flexShrink: 0, alignItems: 'center', flexWrap: 'wrap' }}>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                    {campaign.invited.toLocaleString('pl-PL')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Zaproszeni</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1 }}>
                    {campaign.joined.toLocaleString('pl-PL')}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Dołączyli</Typography>
                </Box>
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="h6" sx={{ fontFamily: theme.typography.h1.fontFamily, lineHeight: 1, color: 'primary.main' }}>
                    {campaign.conversionRate}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">Konwersja</Typography>
                </Box>
              </Box>

              {/* Mini chart */}
              <Box sx={{ width: { xs: '100%', md: 120 }, flexShrink: 0 }}>
                <MiniBarChart data={campaign.weeklyData || []} height={36} />
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, textAlign: 'center' }}>
                  {campaign.weeklyData ? 'Trend tygodniowy' : 'Brak danych'}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      {/* Export */}
      <Button
        variant="outlined"
        size="small"
        startIcon={<FileDownloadOutlinedIcon />}
        disabled={filteredCampaigns.length === 0}
        onClick={() => filteredCampaigns[0] && handleExport(filteredCampaigns[0].id)}
        sx={{ borderRadius: `${theme.tokens?.primitive?.radius?.pill ?? 999}px` }}
      >
        Eksportuj CSV
      </Button>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar((s) => ({ ...s, open: false }))}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default Reports;
